// Meeting chat. Plain elements only (no portalled pickers) so it works the same inside the pop-out window.
import { useEffect, useRef, useState } from 'react';
import { SendHorizontal, X } from 'lucide-react';
import { MAX_CHAT } from './useMeetingChat';

const time = (at) => new Date(at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const ERR = { long: 'That message is too long to send.', failed: "Couldn't send. Check your connection and try again." };

function Message({ m, mine, showHead }) {
  return (
    <div className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`} data-testid="mm-chat-message">
      {showHead && (
        <span className="mb-0.5 text-[11px] text-[#8AA3BC]">
          <span className="font-semibold text-[#E8EEF4]">{mine ? 'You' : m.name}</span> · {time(m.at)}
        </span>
      )}
      <p className={`max-w-[85%] whitespace-pre-wrap break-words rounded-lg px-2.5 py-1.5 text-[13px] leading-snug ${mine ? 'bg-[#4575A8] text-white' : 'bg-[#1E3A52] text-[#E8EEF4]'}`}>
        {m.text}
      </p>
    </div>
  );
}

export default function ChatPanel({ chat, onClose, disabled = false, bare = false, className = '' }) {
  const [draft, setDraft] = useState('');
  const [err, setErr] = useState('');
  const listRef = useRef(null);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat.messages.length]);

  const submit = () => {
    const r = chat.post(draft);
    if (r === 'ok') { setDraft(''); setErr(''); } else if (ERR[r]) setErr(ERR[r]);
  };

  return (
    <div className={`flex min-h-0 flex-col bg-[#0D1B2A] text-[#E8EEF4] ${className}`} data-testid="mm-chat-panel">
      {bare ? (
        <p className="border-b border-[#1E3A52] px-3 py-1.5 text-[10px] text-[#8AA3BC]">Only people in this meeting see it. It's gone when the meeting ends.</p>
      ) : (
      <div className="flex items-center justify-between border-b border-[#1E3A52] px-3 py-2">
        <div>
          <p className="text-sm font-semibold">Chat</p>
          <p className="text-[10px] text-[#8AA3BC]">Only people in this meeting see it. It's gone when the meeting ends.</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close chat" className="rounded p-1 text-[#8AA3BC] hover:bg-white/10 hover:text-white"
          data-testid="mm-chat-close">
          <X className="h-4 w-4" />
        </button>
      </div>
      )}
      <div ref={listRef} className="min-h-0 flex-1 space-y-2 overflow-y-auto px-3 py-3" data-testid="mm-chat-list">
        {chat.messages.length === 0 && <p className="pt-6 text-center text-xs text-[#8AA3BC]" data-testid="mm-chat-empty">No messages yet. Say hi.</p>}
        {chat.messages.map((m, i) => {
          const prev = chat.messages[i - 1];
          const showHead = !prev || prev.from !== m.from || m.at - prev.at > 120000;
          return <Message key={m.id} m={m} mine={m.from === chat.me} showHead={showHead} />;
        })}
      </div>
      <div className="border-t border-[#1E3A52] p-2">
        {err && <p className="mb-1 text-[11px] text-red-300" data-testid="mm-chat-error">{err}</p>}
        <div className="flex items-end gap-1.5">
          <textarea value={draft} rows={1} maxLength={MAX_CHAT} disabled={disabled}
            placeholder={disabled ? "You're not in the call right now" : 'Message everyone'}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
            className="max-h-28 min-h-[36px] flex-1 resize-none rounded-md border border-[#1E3A52] bg-[#0D1B2A] px-2.5 py-2 text-[13px] text-white placeholder:text-[#5F7A94] focus:border-[#4575A8] focus:outline-none"
            data-testid="mm-chat-input" />
          <button type="button" onClick={submit} disabled={disabled || !draft.trim()} aria-label="Send"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#4575A8] text-white transition-colors duration-150 hover:bg-[#3A6491] disabled:opacity-40"
            data-testid="mm-chat-send">
            <SendHorizontal className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
